import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { FileSpreadsheet, Upload, Download, Loader2, X, FileText, CheckCircle2, AlertCircle, RefreshCw, SkipForward, Sparkles, Zap } from 'lucide-react';

export function LeadImportSheet({
  open,
  onOpenChange,
  importStatus,
  importFile,
  importData,
  importProgress,
  importResult,
  isDraggingFile,
  duplicateCheck,
  duplicateStrategy,
  setDuplicateStrategy,
  skipAiScoring,
  setSkipAiScoring,
  checkingDuplicates,
  duplicateCheckFailed,
  fileInputRef,
  onFileDrop,
  onFileDragOver,
  onFileDragLeave,
  onFileInputChange,
  onDownloadSample,
  onImport,
  onReset,
  onTopUp,
}) {
  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onReset(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />Import Leads
          </SheetTitle>
          <SheetDescription>Upload a spreadsheet to import multiple leads at once.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Download Sample */}
          {(importStatus === 'idle' || importStatus === 'error') && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={onDownloadSample} className="gap-2" data-testid="download-sample-button">
                <Download className="h-4 w-4" />Download Sample
              </Button>
            </div>
          )}

          {/* File Upload Zone */}
          {importStatus === 'idle' && (
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                isDraggingFile ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
              }`}
              onDrop={onFileDrop} onDragOver={onFileDragOver} onDragLeave={onFileDragLeave}
              onClick={() => fileInputRef.current?.click()} data-testid="import-drop-zone"
            >
              <div className="flex flex-col items-center gap-3">
                <div className={`p-4 rounded-full transition-colors ${isDraggingFile ? 'bg-primary/10' : 'bg-muted'}`}>
                  <Upload className={`h-8 w-8 ${isDraggingFile ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">{isDraggingFile ? 'Drop your file here' : 'Select a file or drag and drop'}</p>
                  <p className="text-xs text-muted-foreground">Accepts .xlsx, .xls, .csv files</p>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileInputChange} className="hidden" data-testid="import-file-input" />
            </div>
          )}

          {/* Parsing Progress */}
          {importStatus === 'parsing' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Parsing file...</p>
                  <p className="text-xs text-muted-foreground">{importFile?.name}</p>
                </div>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}

          {/* Preview */}
          {importStatus === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{importFile?.name}</p>
                  <p className="text-xs text-muted-foreground">{importData.length} leads found</p>
                </div>
                <Button variant="ghost" size="sm" onClick={onReset} className="h-8 w-8 p-0" data-testid="remove-file-button">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted px-3 py-2 border-b"><p className="text-xs font-medium">Preview (first 5 rows)</p></div>
                <div className="max-h-60 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">Name</th>
                        <th className="text-left p-2 font-medium">Email</th>
                        <th className="text-left p-2 font-medium">Company</th>
                        <th className="text-left p-2 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.slice(0, 5).map((lead, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2 truncate max-w-[100px]">{lead.name || '-'}</td>
                          <td className="p-2 truncate max-w-[120px]">{lead.email || '-'}</td>
                          <td className="p-2 truncate max-w-[100px]">{lead.company || '-'}</td>
                          <td className="p-2">${lead.value || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importData.length > 5 && (
                  <div className="bg-muted/30 px-3 py-2 border-t text-center">
                    <p className="text-xs text-muted-foreground">+{importData.length - 5} more leads</p>
                  </div>
                )}
              </div>

              {/* Duplicate detection */}
              {checkingDuplicates && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg" data-testid="dup-checking">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Checking for duplicates…</p>
                </div>
              )}

              {!checkingDuplicates && duplicateCheck && duplicateCheck.duplicate_count > 0 && (
                <div className="border border-amber-300 dark:border-amber-700 rounded-lg overflow-hidden" data-testid="dup-warning">
                  <div className="flex items-start gap-3 p-3 bg-amber-500/10">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                        {duplicateCheck.duplicate_count} duplicate{duplicateCheck.duplicate_count === 1 ? '' : 's'} found
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {duplicateCheck.new_count} new lead{duplicateCheck.new_count === 1 ? '' : 's'} · matched by email or phone
                      </p>
                    </div>
                  </div>
                  <div className="p-3 space-y-2 border-t border-amber-200 dark:border-amber-800">
                    <p className="text-xs font-medium">How should we handle duplicates?</p>
                    <button
                      type="button"
                      onClick={() => setDuplicateStrategy('update')}
                      className={`w-full flex items-start gap-2 p-2.5 rounded-lg border text-left transition-colors ${duplicateStrategy === 'update' ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/40'}`}
                      data-testid="dup-strategy-update"
                    >
                      <RefreshCw className={`h-4 w-4 mt-0.5 shrink-0 ${duplicateStrategy === 'update' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium">Update / merge existing</span>
                        <span className="block text-[11px] text-muted-foreground">Fill in new details on matching leads (keeps their pipeline stage)</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuplicateStrategy('skip')}
                      className={`w-full flex items-start gap-2 p-2.5 rounded-lg border text-left transition-colors ${duplicateStrategy === 'skip' ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/40'}`}
                      data-testid="dup-strategy-skip"
                    >
                      <SkipForward className={`h-4 w-4 mt-0.5 shrink-0 ${duplicateStrategy === 'skip' ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium">Skip duplicates</span>
                        <span className="block text-[11px] text-muted-foreground">Only import the {duplicateCheck.new_count} new lead{duplicateCheck.new_count === 1 ? '' : 's'}, leave existing untouched</span>
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {!checkingDuplicates && duplicateCheck && duplicateCheck.duplicate_count === 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg" data-testid="dup-none">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <p className="text-xs text-green-700 dark:text-green-400">No duplicates — all leads are new</p>
                </div>
              )}

              {!checkingDuplicates && duplicateCheckFailed && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg" data-testid="dup-check-failed">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Couldn't check for duplicates — existing leads will be updated/merged on import.</p>
                </div>
              )}

              {/* Phase 0 — AI scoring opt-out for cold/campaign uploads */}
              <button
                type="button"
                onClick={() => setSkipAiScoring(!skipAiScoring)}
                className={`w-full flex items-start gap-2 p-2.5 rounded-lg border text-left transition-colors ${skipAiScoring ? 'border-primary bg-primary/5' : 'border-muted hover:bg-muted/40'}`}
                data-testid="skip-ai-scoring-toggle"
                aria-pressed={skipAiScoring}
              >
                <Sparkles className={`h-4 w-4 mt-0.5 shrink-0 ${skipAiScoring ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="min-w-0">
                  <span className="block text-xs font-medium">Import without AI scoring</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Best for large cold/campaign lists — saves credits. Leads are stored and can be scored later on demand.
                  </span>
                </span>
              </button>
            </div>
          )}

          {/* Importing Progress */}
          {importStatus === 'importing' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-lg">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Importing leads...</p>
                  <p className="text-xs text-muted-foreground" data-testid="import-progress-label">
                    {importProgress > 0 ? `${importProgress}% complete` : 'This may take a moment'}
                  </p>
                </div>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}

          {/* Complete */}
          {importStatus === 'complete' && importResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">Import Complete</p>
                  <p className="text-xs text-muted-foreground" data-testid="import-result-summary">
                    {importResult.success_count} imported
                    {importResult.updated_count > 0 ? ` · ${importResult.updated_count} updated` : ''}
                    {importResult.skipped_count > 0 ? ` · ${importResult.skipped_count} skipped` : ''}
                  </p>
                </div>
              </div>

              {/* AI scoring transparency — replaces the old silent 50-lead cap */}
              {importResult.queued_for_ai > 0 && (
                <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg" data-testid="import-ai-queued">
                  <Sparkles className="h-4 w-4 text-primary shrink-0" />
                  <p className="text-xs text-foreground">
                    <span className="font-medium">{importResult.queued_for_ai}</span> lead{importResult.queued_for_ai === 1 ? '' : 's'} queued for AI scoring — your AI will score &amp; build cadences in the background.
                  </p>
                </div>
              )}

              {/* Credit budget hit → upgrade / top-up CTA */}
              {importResult.ai_budget_exhausted && importResult.ai_pending > 0 && (
                <div className="border border-amber-300 dark:border-amber-700 rounded-lg overflow-hidden" data-testid="import-ai-budget-cta">
                  <div className="flex items-start gap-3 p-3 bg-amber-500/10">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                        {importResult.ai_pending} lead{importResult.ai_pending === 1 ? '' : 's'} waiting for AI credits
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        All your leads were imported and saved. {importResult.queued_for_ai > 0 ? `We're scoring the first ${importResult.queued_for_ai} now. ` : ''}
                        Top up or upgrade to let your AI score the rest.
                      </p>
                    </div>
                  </div>
                  {onTopUp && (
                    <div className="p-3 border-t border-amber-200 dark:border-amber-800">
                      <Button size="sm" className="w-full gap-2" onClick={onTopUp} data-testid="import-topup-button">
                        <Zap className="h-4 w-4" />Top up AI credits
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {importResult.error_count > 0 && (
                <div className="p-3 bg-amber-500/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">{importResult.error_count} leads failed</p>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="max-h-32 overflow-auto">
                      {importResult.errors.slice(0, 5).map((err, idx) => (
                        <p key={idx} className="text-xs text-muted-foreground">Row {err.row}: {err.error}</p>
                      ))}
                      {importResult.errors.length > 5 && (
                        <p className="text-xs text-muted-foreground mt-1">+{importResult.errors.length - 5} more errors</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {importStatus === 'error' && (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">Import Failed</p>
                <p className="text-xs text-muted-foreground">Please check your file and try again</p>
              </div>
            </div>
          )}

          {/* Column Guide */}
          {(importStatus === 'idle' || importStatus === 'error') && (
            <div className="bg-muted/30 p-4 rounded-lg space-y-2">
              <p className="text-xs font-medium flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" />Supported Columns
              </p>
              <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                <span>name (required)</span><span>email</span><span>phone</span><span>company</span>
                <span>status</span><span>source</span><span>value</span><span>notes</span>
                <span>address</span><span>city</span><span>state</span><span>zip_code</span>
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="mt-6">
          {importStatus === 'preview' && (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={onReset} className="flex-1" data-testid="cancel-import-button">Cancel</Button>
              <Button onClick={onImport} className="flex-1" disabled={checkingDuplicates} data-testid="confirm-import-button">
                <Upload className="h-4 w-4 mr-2" />
                {duplicateStrategy === 'skip' && duplicateCheck && duplicateCheck.duplicate_count > 0
                  ? `Import ${duplicateCheck.new_count} New`
                  : `Import ${importData.length} Leads`}
              </Button>
            </div>
          )}
          {importStatus === 'complete' && (
            <Button onClick={() => { onOpenChange(false); onReset(); }} className="w-full" data-testid="done-import-button">Done</Button>
          )}
          {importStatus === 'error' && (
            <Button variant="outline" onClick={onReset} className="w-full" data-testid="retry-import-button">Try Again</Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
